import {NavLink, useParams} from 'react-router-dom';
import {Plug, ShieldCheck, UserRound, Users, Database, Info, ChevronRight} from 'lucide-react';
import {useApi} from '../../lib/api';
import {ErrorNotice, Loading, Notice, PageHeading, Panel, StatusBadge} from '../../components/ui/common';
import {useAuth} from '../auth/auth';
import {PasskeySetup} from '../auth/passkey-setup';
import {UsersPanel} from './users-panel';
import {MigrationPanel} from './initial-migration';
import {SystemPanel} from './system-panel';
import './settings.css';
interface Settings { mode: 'live' | 'shadow'; storageConfigured: boolean }
const sections = [
  {id: 'account', label: 'Your account', icon: UserRound},
  {id: 'migration', label: 'Initial migration', icon: Database, admin: true},
  {id: 'workspace', label: 'Workspace status', icon: ShieldCheck},
  {id: 'users', label: 'Users', icon: Users, admin: true},
  {id: 'connections', label: 'Connections', icon: Plug, admin: true},
  {id: 'system', label: 'System', icon: Info},
];
function WorkspaceStatus({ mode, storageConfigured }: Settings) {
  return <Panel title="Workspace status" description="Status of external automations and optional supporting-file uploads.">
    <div className="status-list">
      <div><ShieldCheck size={19} /><div><strong>Automation mode</strong><p>{mode === 'live'
        ? 'Live mode permits provider access. Each service still needs to be enabled and configured in Connections.'
        : 'Shadow mode blocks external provider requests. Automations can still use saved test data.'}</p></div><StatusBadge status={mode} /></div>
      <div><Database size={19} /><div><strong>Evidence storage</strong><p>{storageConfigured
        ? 'Storage settings are present for file attachments. This status does not test an upload.'
        : 'File attachments are unavailable until storage is configured. Investment records still work.'}</p></div><StatusBadge status={storageConfigured ? 'configured' : 'not configured'} /></div>
    </div>
  </Panel>;
}
export function SettingsPage() {
  const {user} = useAuth();
  const {section = 'account'} = useParams();
  const settings = useApi<Settings>('/settings');
  const allowed = sections.filter(item => !item.admin || user.role === 'admin');
  return <><PageHeading eyebrow="Workspace preferences" title="Settings" description="Manage your account, workspace and system information." />
    <div className="settings-layout"><nav className="settings-navigation" aria-label="Settings sections">{allowed.map(({id, label, icon: Icon}) => <NavLink key={id} to={id === 'account' ? '/settings' : '/settings/' + id} end><Icon size={18} /><span>{label}</span><ChevronRight size={16} /></NavLink>)}</nav>
      <div className="settings-content">
        {!allowed.some(item => item.id === section) && <Notice>This settings section is unavailable.</Notice>}
        {section === 'account' && <Panel className="account-panel" title="Your account" description="Passkeys are scoped to this application’s domain."><div className="account-profile"><span className="avatar">{(user.name ?? user.email).slice(0, 2).toUpperCase()}</span><div><h3>{user.name ?? user.email}</h3><p>{user.email}</p><StatusBadge status={user.role} /></div></div><PasskeySetup /></Panel>}
        {section === 'system' && <SystemPanel />}
        {section === 'migration' && user.role === 'admin' && <MigrationPanel />}
        {section === 'users' && user.role === 'admin' && <UsersPanel />}
        {section === 'workspace' && (settings.isPending ? <Loading /> : settings.error ? <ErrorNotice error={settings.error} retry={() => {void settings.refetch();}} /> : <WorkspaceStatus {...settings.data} />)}
      </div>
    </div></>;
}
